/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @file 通用错误处理核心逻辑
 */

import type {ModalStaticFunctions} from 'antd/es/modal/confirm';
import type {ArgsProps} from 'antd/es/notification/interface';
import {BizError} from '@/utils/Error/BizError';

export interface IBaseHandleErrorOptions {
    /**
     * 消息展示 API，可以是 message 或 notification 实例
     * 两者 API 兼容，支持 error/success/warning/info 等方法
     */
    messageApi?: {
        error: (config: string | Partial<ArgsProps> | any) => void;
        success?: (config: string | Partial<ArgsProps> | any) => void;
        warning?: (config: string | Partial<ArgsProps> | any) => void;
        info?: (config: string | Partial<ArgsProps> | any) => void;
    };
    modalApi?: Omit<ModalStaticFunctions, 'warn'> | ModalStaticFunctions;
    navigate?: (path: string) => void;
    customMessage?: string;
    /**
     * 通知配置项（支持所有 Ant Design notification 配置）
     */
    messageConfig?: Partial<ArgsProps>;
}

/**
 * 提取错误信息
 */
function getErrorMessage(error: unknown, customMessage?: string): string {
    // 自定义消息优先
    if (customMessage) {
        return customMessage;
    }

    // BizError 有 message 属性（后端 API 错误）
    if (error instanceof BizError) {
        return error.message || '请求发生错误';
    }

    // ky TimeoutError / fetch AbortError（请求超时）
    if (error instanceof Error && (error.name === 'TimeoutError' || error.message?.includes('timed out'))) {
        return '请求超时，请稍后重试';
    }

    // 标准 Error 有 message 属性（前端逻辑错误）
    if (error instanceof Error) {
        return error.message || '请求失败';
    }

    // 未知错误
    return '请求失败';
}

/**
 * 判断是否为前端逻辑错误（需要打印详细日志）
 */
function isFrontendError(error: unknown): boolean {
    // BizError 是后端 API 错误，不需要额外日志
    if (error instanceof BizError) {
        return false;
    }

    // 超时错误需要弹窗提示，不视为静默的前端错误
    if (error instanceof Error && (error.name === 'TimeoutError' || error.message?.includes('timed out'))) {
        return false;
    }

    // 标准 Error 是前端逻辑错误
    if (error instanceof Error) {
        return true;
    }

    // 未知类型的错误也视为前端错误
    return true;
}

/**
 * 通用错误处理逻辑
 */
export async function baseHandleError(error: unknown, options: IBaseHandleErrorOptions = {}) {
    const {messageApi, customMessage, messageConfig} = options;

    const errorMessage = getErrorMessage(error, customMessage);

    // 【核心逻辑】前端逻辑错误 → 只打印日志，不弹窗
    if (isFrontendError(error)) {
        if (error instanceof Error) {
            console.error('堆栈信息:', error);
        }
        // eslint-disable-next-line no-console
        console.groupEnd();
        return; // 直接返回，不弹窗
    }

    // 如果没有 messageApi，只能打印到控制台
    if (!messageApi) {
        console.error(errorMessage, error);
        return;
    }

    // 【只有后端 API 错误才会走到这里】统一弹窗提示
    const finalConfig = {
        content: errorMessage, // message API: 展示内容       // notification API: 标题（默认）
        description: errorMessage, // notification API: 描述（默认）
        ...messageConfig // 展开所有用户自定义配置（会覆盖上面的默认值）
    };
    messageApi.error(finalConfig);
}
